import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Button, Typography, Alert, Row, Col, Divider, Input, Select, Form, Result, Spin } from 'antd';
import UilLeft from '@iconscout/react-unicons/icons/uil-arrow-left';
import { ManualPaymentService } from '../../services/manualPaymentService';
import { PageHeader } from '../page-headers/page-headers';
import { Main } from '../../container/styled';
import { OwnerDetailsBox } from '../../container/borrower/project/projectDetail/Style';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PageRoutes = [
  {
    path: 'index',
    breadcrumbName: 'Dashboard',
  },
  {
    path: 'borrower',
    breadcrumbName: 'Borrower',
  },
  {
    path: '',
    breadcrumbName: 'Deposit',
  },
];

function ManualBorrowerDepositPage() {
  const navigate = useNavigate();
  const { borrowerId } = useSelector((state) => state.auth);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [transactionId, setTransactionId] = useState(null);

  const handleInitiatePayment = () => {
    setShowPaymentForm(true);
  };

  const handleSubmitPayment = async (values) => {
    if (!borrowerId || !values.amount || parseFloat(values.amount) <= 0) {
      setError('Please enter a valid amount to deposit');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const paymentData = {
        amount: parseFloat(values.amount), // Ensure amount is a number
        currency: values.currency,
        borrowerId,
        paymentMethod: values.paymentMethod,
        referenceCode: values.referenceCode,
        notes: values.notes || '',
      };

      const { data, error: submitError } = await ManualPaymentService.submitBorrowerDeposit(paymentData);

      if (submitError || !data) {
        throw new Error(submitError || 'Failed to submit manual deposit');
      }

      // Store transaction ID for reference
      if (data.transactionId) {
        setTransactionId(data.transactionId);
      }

      setPaymentSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to process deposit');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const goToDashboard = () => {
    navigate('/borrower/wallet/details');
  };

  if (loading && !showPaymentForm) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <>
        <PageHeader className="ninjadash-page-header-main" title={<h4>Deposit Submitted</h4>} routes={PageRoutes} />
        <Main style={{ minHeight: 'auto' }}>
          <Result
            status="success"
            title="Deposit Successfully Submitted!"
            subTitle={
              <>
                Your deposit has been recorded and processed immediately. Your account balance has been updated.
                {transactionId && (
                  <p>
                    <strong>Transaction ID:</strong> {transactionId}
                  </p>
                )}
              </>
            }
            extra={[
              <Button type="primary" key="dashboard" onClick={goToDashboard}>
                Go to Wallet
              </Button>,
              <Button key="back" onClick={goBack}>
                Go Back
              </Button>,
            ]}
          />
        </Main>
      </>
    );
  }

  return (
    <>
      <PageHeader className="ninjadash-page-header-main" title={<h4>Deposit Funds</h4>} routes={PageRoutes} />
      <Main style={{ minHeight: 'auto' }}>
        <OwnerDetailsBox>
          <Row gutter={15}>
            <Col xs={24}>
              <a
                className="ninjadash-back-btn"
                onClick={(e) => {
                  e.preventDefault();
                  goBack();
                }}
                href="#"
              >
                <UilLeft /> Go back
              </a>
            </Col>

            {error && (
              <Col xs={24} style={{ marginBottom: '20px' }}>
                <Alert message={error} type="error" showIcon />
              </Col>
            )}

            <Col xs={24} md={16} lg={12} className="section-margin" style={{ margin: '0 auto' }}>
              <Card title="Deposit Details" bordered={false}>
                {showPaymentForm ? (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmitPayment}
                    initialValues={{
                      currency: 'KES',
                    }}
                  >
                    <Form.Item
                      name="amount"
                      label="Amount"
                      rules={[{ required: true, message: 'Please enter an amount' }]}
                    >
                      <Input type="number" prefix="$" placeholder="0.00" min={0} step="0.01" disabled={loading} />
                    </Form.Item>

                    <Form.Item name="currency" label="Currency">
                      <Select disabled={loading}>
                        <Option value="KES">KES</Option>
                        <Option value="EUR">EUR</Option>
                        <Option value="GBP">GBP</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="paymentMethod"
                      label="Payment Method"
                      rules={[{ required: true, message: 'Please select payment method' }]}
                    >
                      <Select placeholder="Select payment method" disabled={loading}>
                        <Option value="Bank Transfer">Bank Transfer</Option>
                        <Option value="Check">Check</Option>
                        <Option value="Cash Deposit">Cash Deposit</Option>
                        <Option value="Wire Transfer">Wire Transfer</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="referenceCode"
                      label="Reference/Transaction Code"
                      rules={[{ required: true, message: 'Please enter reference code' }]}
                    >
                      <Input placeholder="Enter bank reference or transaction code" disabled={loading} />
                    </Form.Item>

                    <Form.Item name="notes" label="Additional Notes">
                      <TextArea
                        rows={4}
                        placeholder="Enter any additional payment details or notes"
                        disabled={loading}
                      />
                    </Form.Item>

                    <Divider style={{ margin: '12px 0' }} />

                    <Form.Item>
                      <Button type="primary" size="large" block htmlType="submit" loading={loading}>
                        {loading ? 'Processing...' : 'Submit Deposit Details'}
                      </Button>
                    </Form.Item>

                    <Text
                      type="secondary"
                      style={{ fontSize: '12px', display: 'block', marginTop: '12px', textAlign: 'center' }}
                    >
                      Your deposit will be processed immediately and added to your account balance.
                    </Text>
                  </Form>
                ) : (
                  <div>
                    <Title level={5} style={{ marginBottom: '24px' }}>
                      Add funds to your account for project financing
                    </Title>

                    <Button type="primary" size="large" block onClick={handleInitiatePayment}>
                      Make Manual Deposit
                    </Button>

                    <Divider style={{ margin: '24px 0' }}>
                      <Text type="secondary">Deposit Instructions</Text>
                    </Divider>

                    <div style={{ marginBottom: '24px' }}>
                      <Text>
                        <strong>Bank Name:</strong> Global Investment Bank
                        <br />
                        <strong>Account Name:</strong> Investment Platform Holdings
                        <br />
                        <strong>Account Number:</strong> 1234567890
                        <br />
                        <strong>SWIFT/BIC Code:</strong> GLIBUSXX
                        <br />
                        <strong>Reference:</strong> BOR-{borrowerId}
                      </Text>
                    </div>

                    <Alert
                      message="Important"
                      description="Please ensure you include Your manager ID in the payment reference to help us identify your deposit."
                      type="info"
                      showIcon
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </OwnerDetailsBox>
      </Main>
    </>
  );
}

export default ManualBorrowerDepositPage;
